<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('image.title') }}</h1>
      <p>{{ $t('image.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="upload-area" @click="triggerUpload" @dragover.prevent @drop.prevent="handleDrop">
        <input type="file" ref="fileInput" @change="handleFileSelect" accept="image/*" hidden>
        <div class="upload-content" v-if="!imageData">
          <div class="upload-icon">📁</div>
          <p>{{ $t('image.uploadHint') }}</p>
          <span>{{ $t('image.supportedFormats') }}</span>
        </div>
        <div class="image-preview" v-else>
          <canvas ref="canvas"></canvas>
        </div>
      </div>

      <div class="controls" v-if="imageData">
        <div class="control-section">
          <h3>{{ $t('image.basicAdjust') }}</h3>
          <div class="control-row">
            <label>{{ $t('image.quality') }}</label>
            <input type="range" v-model="quality" min="10" max="100" step="5">
            <span>{{ quality }}%</span>
          </div>
          <div class="control-row">
            <label>{{ $t('image.scale') }}</label>
            <input type="range" v-model="scale" min="10" max="100" step="5">
            <span>{{ scale }}%</span>
          </div>
        </div>

        <div class="control-section">
          <h3>{{ $t('image.filterEffects') }}</h3>
          <div class="filters-grid">
            <button 
              v-for="filter in filters" 
              :key="filter.name"
              :class="['filter-btn', { active: currentFilter === filter.name }]"
              @click="applyFilter(filter.name)"
            >
              {{ $t('image.filters.' + filter.name) }}
            </button>
          </div>
        </div>

        <div class="control-section">
          <h3>{{ $t('image.imageInfo') }}</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">{{ $t('image.originalSize') }}</span>
              <span class="info-value">{{ originalWidth }} × {{ originalHeight }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t('image.outputSize') }}</span>
              <span class="info-value">{{ outputWidth }} × {{ outputHeight }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t('image.originalFileSize') }}</span>
              <span class="info-value">{{ formatSize(originalSize) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t('image.estimatedSize') }}</span>
              <span class="info-value">{{ formatSize(estimatedSize) }}</span>
            </div>
          </div>
        </div>

        <div class="actions">
          <button class="btn primary" @click="downloadImage">{{ $t('image.download') }}</button>
          <button class="btn" @click="resetImage">{{ $t('image.reset') }}</button>
          <button class="btn" @click="clearImage">{{ $t('image.clear') }}</button>
        </div>
      </div>
    </div>

    <!-- 工具说明 -->
    <div class="tool-description">
      <h2>{{ $t('image.whyCompress') }}</h2>
      <p>{{ $t('image.whyCompressDesc') }}</p>

      <h2>{{ $t('image.formatGuide') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('image.formatGuideItems')" :key="'format'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('image.compressionTips') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('image.compressionTipsItems')" :key="'tip'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('image.privacyNote') }}</h2>
      <p>{{ $t('image.privacyNoteDesc') }}</p>

      <h2>{{ $t('image.webOptimization') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('image.webOptimizationItems')" :key="'opt'+index">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageTool',
  data() {
    return {
      imageData: null,
      originalImage: null,
      originalWidth: 0,
      originalHeight: 0,
      originalSize: 0,
      quality: 80,
      scale: 100,
      currentFilter: 'none',
      filters: [
        { name: 'none' },
        { name: 'grayscale' },
        { name: 'sepia' },
        { name: 'invert' },
        { name: 'blur' },
        { name: 'brightness' },
        { name: 'contrast' },
        { name: 'saturate' }
      ]
    }
  },
  computed: {
    outputWidth() {
      return Math.round(this.originalWidth * this.scale / 100)
    },
    outputHeight() {
      return Math.round(this.originalHeight * this.scale / 100)
    },
    estimatedSize() {
      return Math.round(this.originalSize * (this.quality / 100) * (this.scale / 100) ** 2)
    }
  },
  watch: {
    quality() {
      this.renderImage()
    },
    scale() {
      this.renderImage()
    }
  },
  methods: {
    triggerUpload() {
      if (!this.imageData) {
        this.$refs.fileInput.click()
      }
    },

    handleDrop(e) {
      const file = e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.loadImage(file)
      }
    },

    handleFileSelect(e) {
      const file = e.target.files[0]
      if (file) {
        this.loadImage(file)
      }
    },

    loadImage(file) {
      this.originalSize = file.size
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          this.originalImage = img
          this.originalWidth = img.width
          this.originalHeight = img.height
          this.imageData = e.target.result
          this.$nextTick(() => {
            this.renderImage()
          })
        }
        img.src = e.target.result
      }
      reader.readAsDataURL(file)
    },

    renderImage() {
      if (!this.originalImage) return

      const canvas = this.$refs.canvas
      const ctx = canvas.getContext('2d')
      
      const width = this.outputWidth
      const height = this.outputHeight
      
      canvas.width = width
      canvas.height = height

      // Apply filter
      ctx.filter = this.getFilterStyle()
      ctx.drawImage(this.originalImage, 0, 0, width, height)
      ctx.filter = 'none'
    },

    getFilterStyle() {
      switch (this.currentFilter) {
        case 'grayscale': return 'grayscale(100%)'
        case 'sepia': return 'sepia(100%)'
        case 'invert': return 'invert(100%)'
        case 'blur': return 'blur(2px)'
        case 'brightness': return 'brightness(130%)'
        case 'contrast': return 'contrast(150%)'
        case 'saturate': return 'saturate(200%)'
        default: return 'none'
      }
    },

    applyFilter(filterName) {
      this.currentFilter = filterName
      this.renderImage()
    },

    resetImage() {
      this.quality = 80
      this.scale = 100
      this.currentFilter = 'none'
      this.renderImage()
    },

    clearImage() {
      this.imageData = null
      this.originalImage = null
      this.originalWidth = 0
      this.originalHeight = 0
      this.originalSize = 0
      this.quality = 80
      this.scale = 100
      this.currentFilter = 'none'
      this.$refs.fileInput.value = ''
    },

    downloadImage() {
      const canvas = this.$refs.canvas
      const link = document.createElement('a')
      link.download = `processed_image_${Date.now()}.jpg`
      link.href = canvas.toDataURL('image/jpeg', this.quality / 100)
      link.click()
    },

    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
    }
  }
}
</script>

<style scoped>
.tool-page {
  max-width: 900px;
  margin: 0 auto;
}

.tool-header {
  text-align: center;
  margin-bottom: 32px;
}

.tool-header h1 {
  font-size: 32px;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.tool-header p {
  color: var(--text-secondary);
  font-size: 16px;
}

.tool-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-md);
}

.upload-area {
  border: 2px dashed var(--border-color);
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 24px;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area:hover {
  border-color: var(--accent-color);
  background: var(--accent-light);
}

.upload-content {
  color: var(--text-secondary);
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.upload-content p {
  font-size: 18px;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.upload-content span {
  font-size: 14px;
}

.image-preview {
  max-width: 100%;
  overflow: hidden;
}

.image-preview canvas {
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
}

.controls {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.control-section h3 {
  font-size: 16px;
  margin-bottom: 16px;
  color: var(--text-primary);
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.control-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.control-row label {
  width: 80px;
  color: var(--text-secondary);
  font-size: 14px;
}

.control-row input[type="range"] {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: var(--bg-tertiary);
  appearance: none;
  cursor: pointer;
}

.control-row input[type="range"]::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--accent-color);
  cursor: pointer;
}

.control-row span {
  width: 50px;
  text-align: right;
  color: var(--text-primary);
  font-weight: 500;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

@media (max-width: 600px) {
  .filters-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.filter-btn {
  padding: 10px 16px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.filter-btn:hover {
  background: var(--bg-hover);
}

.filter-btn.active {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 12px;
  background: var(--bg-tertiary);
  border-radius: 8px;
}

.info-label {
  color: var(--text-secondary);
  font-size: 13px;
}

.info-value {
  color: var(--text-primary);
  font-weight: 500;
  font-size: 13px;
}

.actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.btn {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 24px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn:hover {
  background: var(--bg-hover);
}

.btn.primary {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
}

.btn.primary:hover {
  background: var(--accent-hover);
}

.tool-description {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  margin-top: 32px;
  box-shadow: var(--shadow-md);
}

.tool-description h2 {
  font-size: 20px;
  color: var(--text-primary);
  margin: 28px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.tool-description h2:first-child {
  margin-top: 0;
}

.tool-description p {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 12px;
}

.tool-description ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tool-description li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
}

.tool-description li::before {
  content: '•';
  color: var(--accent-color);
  position: absolute;
  left: 0;
}
</style>
